/**
 * Performance Monitor
 * Tracks all operations, API calls, and renders for performance optimization
 */

interface PerformanceLog {
  timestamp: number
  page: string
  operation: string
  duration?: number
  details?: any
  type: 'api' | 'render' | 'cache' | 'hook' | 'component' | 'navigation'
}

class PerformanceMonitor {
  private logs: PerformanceLog[] = []
  private pageLoadStart: number | null = null
  private currentPage: string = ''
  private enabled: boolean = true

  constructor() {
    // Enable only in development
    this.enabled = import.meta.env.DEV
  }

  startPageLoad(page: string) {
    if (!this.enabled) return
    
    this.currentPage = page
    this.pageLoadStart = performance.now()
    
    console.group(`📊 [PERF] Page Load Started: ${page}`)
    console.log(`⏱️ Time: ${new Date().toLocaleTimeString()}`)
  }

  endPageLoad() {
    if (!this.enabled || !this.pageLoadStart) return
    
    const duration = performance.now() - this.pageLoadStart
    
    this.log({
      timestamp: Date.now(),
      page: this.currentPage,
      operation: 'Page Load Complete',
      duration,
      type: 'navigation'
    })

    console.log(`✅ Page Load Complete: ${duration.toFixed(2)}ms`)
    console.groupEnd()
    
    this.pageLoadStart = null
  }

  logApiCall(endpoint: string, method: string, startTime: number, cached: boolean = false) {
    if (!this.enabled) return
    
    const duration = performance.now() - startTime
    const cacheStatus = cached ? '🟢 CACHED' : '🔴 NETWORK'
    
    this.log({
      timestamp: Date.now(),
      page: this.currentPage,
      operation: `API ${method} ${endpoint}`,
      duration,
      details: { method, endpoint, cached },
      type: 'api'
    })

    console.log(`${cacheStatus} API ${method} ${endpoint} - ${duration.toFixed(2)}ms`)
  }

  logHookExecution(hookName: string, startTime: number, details?: any) {
    if (!this.enabled) return
    
    const duration = performance.now() - startTime
    
    this.log({
      timestamp: Date.now(),
      page: this.currentPage,
      operation: `Hook: ${hookName}`,
      duration,
      details,
      type: 'hook'
    })

    console.log(`🪝 Hook ${hookName} - ${duration.toFixed(2)}ms`, details || '')
  }

  logComponentRender(componentName: string, renderCount: number) {
    if (!this.enabled) return
    
    this.log({
      timestamp: Date.now(),
      page: this.currentPage,
      operation: `Render: ${componentName}`,
      details: { renderCount },
      type: 'render'
    })

    if (renderCount > 3) {
      console.warn(`⚠️ ${componentName} rendered ${renderCount} times - Possible optimization needed`)
    } else {
      console.log(`🎨 ${componentName} rendered (${renderCount}x)`)
    }
  }

  logCacheHit(key: string, source: string = 'React Query') {
    if (!this.enabled) return
    
    this.log({
      timestamp: Date.now(),
      page: this.currentPage,
      operation: `Cache Hit: ${key}`,
      details: { source },
      type: 'cache'
    })

    console.log(`💾 Cache Hit: ${key} (${source})`)
  }

  logCacheMiss(key: string, source: string = 'React Query') {
    if (!this.enabled) return
    
    this.log({
      timestamp: Date.now(),
      page: this.currentPage,
      operation: `Cache Miss: ${key}`,
      details: { source },
      type: 'cache'
    })

    console.log(`❌ Cache Miss: ${key} (${source})`)
  }

  logUnnecessaryReload(resource: string, reason: string) {
    if (!this.enabled) return
    
    this.log({
      timestamp: Date.now(),
      page: this.currentPage,
      operation: `Unnecessary Reload: ${resource}`,
      details: { reason },
      type: 'api'
    })

    console.warn(`⚠️ UNNECESSARY RELOAD: ${resource} - ${reason}`)
  }

  private log(entry: PerformanceLog) {
    this.logs.push(entry)
    
    // Keep only last 100 logs
    if (this.logs.length > 100) {
      this.logs.shift()
    }
  }

  getPageStats(page: string) {
    const pageLogs = this.logs.filter(log => log.page === page)
    
    const apiCalls = pageLogs.filter(log => log.type === 'api')
    const cacheHits = pageLogs.filter(log => log.type === 'cache' && log.operation.includes('Hit'))
    const cacheMisses = pageLogs.filter(log => log.type === 'cache' && log.operation.includes('Miss'))
    const renders = pageLogs.filter(log => log.type === 'render')
    
    const totalApiTime = apiCalls.reduce((sum, log) => sum + (log.duration || 0), 0)
    const avgApiTime = apiCalls.length > 0 ? totalApiTime / apiCalls.length : 0
    
    return {
      page,
      totalApiCalls: apiCalls.length,
      totalApiTime: totalApiTime.toFixed(2),
      avgApiTime: avgApiTime.toFixed(2),
      cacheHits: cacheHits.length,
      cacheMisses: cacheMisses.length,
      cacheHitRate: cacheHits.length + cacheMisses.length > 0 
        ? ((cacheHits.length / (cacheHits.length + cacheMisses.length)) * 100).toFixed(1)
        : '0',
      totalRenders: renders.length
    }
  }

  printPageStats(page: string) {
    if (!this.enabled) return
    
    const stats = this.getPageStats(page)
    
    console.group(`📊 Performance Stats: ${page}`)
    console.table(stats)
    console.groupEnd()
  }

  printAllStats() {
    if (!this.enabled) return
    
    const pages = [...new Set(this.logs.map(log => log.page))]
    const allStats = pages.map(page => this.getPageStats(page))
    
    console.group('📊 All Pages Performance Stats')
    console.table(allStats)
    console.groupEnd()
  }

  getRecommendations(page: string) {
    const stats = this.getPageStats(page)
    const recommendations: string[] = []
    
    // Check cache hit rate
    const hitRate = parseFloat(stats.cacheHitRate)
    if (hitRate < 50) {
      recommendations.push(`⚠️ Low cache hit rate (${stats.cacheHitRate}%) - Consider increasing staleTime`)
    }
    
    // Check API calls
    if (stats.totalApiCalls > 5) {
      recommendations.push(`⚠️ High number of API calls (${stats.totalApiCalls}) - Consider batching or caching`)
    }
    
    // Check average API time
    const avgTime = parseFloat(stats.avgApiTime)
    if (avgTime > 500) {
      recommendations.push(`⚠️ Slow API calls (avg ${stats.avgApiTime}ms) - Consider optimization or pagination`)
    }
    
    // Check renders
    if (stats.totalRenders > 10) {
      recommendations.push(`⚠️ High render count (${stats.totalRenders}) - Check for unnecessary re-renders`)
    }
    
    if (recommendations.length === 0) {
      recommendations.push('✅ Performance looks good!')
    }
    
    return recommendations
  }

  printRecommendations(page: string) {
    if (!this.enabled) return
    
    const recommendations = this.getRecommendations(page)
    
    console.group(`💡 Recommendations: ${page}`)
    recommendations.forEach(rec => console.log(rec))
    console.groupEnd()
  }

  clear() {
    this.logs = []
    console.clear()
  }

  // Export logs for analysis
  exportLogs() {
    return JSON.stringify(this.logs, null, 2)
  }
}

// Singleton instance
export const perfMonitor = new PerformanceMonitor()

// Make it available in console for debugging
if (typeof window !== 'undefined') {
  (window as any).perfMonitor = perfMonitor
}
